import { BtnSecondary, Button } from '@components/buttons';
import { IcoClock, IcoMicrophone, IcoRocketLaunch } from '@components/icons';
import { LoadedProps, RouteLoadProps, useRouteParams, useRouter } from '@components/router';
import { useEffect, useState } from 'preact/hooks';
import { useAuth } from 'client/lib/auth';
import { showError } from '@components/app-error';
import { ComponentChildren } from 'preact';
import { hasLevel } from 'shared/auth';
import { defCalRoute } from '../common';
import { StartInterviewModal } from './start-interview-modal';
import { InterviewConversation } from './interview-conversation';
import { getAiInterview, saveAiInterview } from './local-data';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';

export const route = defCalRoute({ Page, hideChat: true, load, isPublic: true });

async function load({ auth }: RouteLoadProps) {
  return getAiInterview(auth.user?.name);
}

function Benefit({ title, icon }: { title: string; icon: ComponentChildren }) {
  return (
    <div class="flex flex-col items-center justify-center text-gray-100 p-4 gap-1">
      {icon}
      <h3 class="text-sm lg:text-base text-gray-100">{title}</h3>
    </div>
  );
}

function Page({ state, setState }: LoadedProps<typeof load>) {
  const router = useRouter();
  const auth = useAuth();
  const user = auth.user;
  const { shouldStart } = useRouteParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showStartingModal, setShowStartingModal] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [firstName, setFirstName] = useState(state.name);

  const isResuming = !!state?.name;

  useDidUpdateEffect(() => {
    saveAiInterview(state);
  }, [state]);

  useEffect(() => {
    if (shouldStart === 'true') {
      const name = user?.displayName || user?.name || state.name;
      startInterview(name);
    }
  }, [shouldStart]);

  useEffect(() => {
    if (user?.calendarTier === 'core' && user.level !== 'superadmin') {
      router.goto('/calendar/page-builder');
    }
  }, [user]);

  function initInterview(firstName: string) {
    setState(() => ({
      messages: [],
      name: firstName,
      landingPage: undefined,
      profile: { sections: {} },
    }));
  }

  function startInterview(name: string) {
    setState((s) => ({ ...s, name }));
    setFirstName(name);
    setConversationStarted(true);
  }

  if (conversationStarted) {
    return (
      <InterviewConversation
        firstName={firstName}
        initialMessages={state?.messages || []}
        initialProfile={state?.profile}
      />
    );
  }

  return (
    <>
      {showStartingModal && (
        <StartInterviewModal hide={() => setShowStartingModal(false)} onStart={startInterview} />
      )}
      <div class="flex h-screen items-center justify-center bg-linear-to-br from-indigo-500 to-indigo-300">
        <div class="px-4 py-24 sm:py-32">
          <div class="mx-auto lg:max-w-4xl text-center text-white">
            <h2 class="text-4xl lg:text-5xl font-semibold tracking-tight lg:leading-20 text-balance text-white">
              Coaches and Solopreneurs: Launch a Landing Page in Minutes & Start Booking Clients
            </h2>
            <p class="p-6 text-lg lg:text-xl text-pretty text-gray-100">
              Pinpoint your niche, dial in your messaging, and instantly launch a landing page that
              converts – no coding required.
            </p>
            <div class="flex justify-center lg:gap-8 mt-4">
              <Benefit
                title="Voice-Powered Setup"
                icon={<IcoMicrophone class="size-6 lg:size-8" />}
              />
              <Benefit title="Save Hours Weekly" icon={<IcoClock class="size-6 lg:size-8" />} />
              <Benefit
                title="Launch in Minutes"
                icon={<IcoRocketLaunch class="size-6 lg:size-8" />}
              />
            </div>
            <footer class="flex flex-col items-center justify-center gap-4 mt-8">
              <Button
                class="rounded-full px-10 py-4 bg-white text-indigo-600 font-semibold text-lg hover:bg-gray-100 transition duration-500 hover:scale-110"
                isLoading={isLoading}
                onClick={() => {
                  if (state.name) {
                    const name = user?.displayName || user?.name || state.name;
                    startInterview(name);
                  } else {
                    setShowStartingModal(true);
                  }
                }}
              >
                Go Live in Minutes →
              </Button>
              {hasLevel(user, 'superadmin') && isResuming && (
                <div class="flex gap-4">
                  <BtnSecondary
                    class="rounded-full pr-8 pl-6 bg-white"
                    isLoading={isLoading}
                    onClick={async () => {
                      setIsLoading(true);
                      try {
                        initInterview(state.name);
                        window.location.reload();
                      } catch (err) {
                        showError(err);
                        setIsLoading(false);
                      }
                    }}
                  >
                    Reset Interview
                  </BtnSecondary>
                </div>
              )}
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}
